'use client';
import React, { useState } from 'react';
import { Swords, Zap, Users, Clock, DollarSign, Trophy, Wifi, ChevronRight } from 'lucide-react';
import type { Player, GameType } from './MatchRoomContent';

interface Props {
  self: Player;
  opponent: Player;
  rankColors: Record<string, string>;
  onStart: (type: GameType) => void;
}

const GAME_OPTIONS: { type: GameType; label: string; desc: string; icon: React.ReactNode; duration: string; rounds: number }[] = [
  { type: 'tictactoe', label: 'Tic Tac Toe', desc: 'Classic strategy — first to 3 in a row wins', icon: <span className="text-2xl">⚔️</span>, duration: '~3 min', rounds: 3 },
  { type: 'math', label: 'Math Speed Quiz', desc: 'Solve arithmetic faster than your opponent', icon: <span className="text-2xl">🧮</span>, duration: '~2 min', rounds: 3 },
];

export default function MatchLobby({ self, opponent, rankColors, onStart }: Props) {
  const [selectedGame, setSelectedGame] = useState<GameType>('tictactoe');
  const [entryMode, setEntryMode] = useState<'free' | 'paid'>('free');

  return (
    <div className="max-w-2xl mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-3"
          style={{ background: 'rgba(124,58,237,0.15)', color: '#A78BFA', border: '1px solid rgba(124,58,237,0.3)' }}>
          <Swords size={12} /> 1-vs-1 Match Room
        </div>
        <h1 className="text-2xl font-black text-foreground mb-1">Challenge Accepted</h1>
        <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Real-time competitive match with live validation</p>
      </div>

      {/* Players */}
      <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="flex items-center justify-between">
          <PlayerCard player={self} rankColors={rankColors} side="left" />
          <div className="flex flex-col items-center gap-2">
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-lg font-black"
              style={{ background: 'linear-gradient(135deg, #7C3AED, #06B6D4)', color: '#fff' }}>VS</div>
            <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Rating diff: {Math.abs(self.rating - opponent.rating)}</span>
          </div>
          <PlayerCard player={opponent} rankColors={rankColors} side="right" isOpponent />
        </div>

        {/* Connection status */}
        <div className="flex items-center justify-center gap-4 mt-4 pt-4 border-t" style={{ borderColor: 'var(--border)' }}>
          <span className="flex items-center gap-1.5 text-xs" style={{ color: '#10B981' }}>
            <Wifi size={12} /> You: Connected
          </span>
          <span className="w-px h-4" style={{ background: 'var(--border)' }} />
          <span className="flex items-center gap-1.5 text-xs" style={{ color: '#10B981' }}>
            <Wifi size={12} /> {opponent.name}: Online
          </span>
          <span className="w-px h-4" style={{ background: 'var(--border)' }} />
          <span className="flex items-center gap-1.5 text-xs" style={{ color: '#A78BFA' }}>
            <Users size={12} /> 14 spectators
          </span>
        </div>
      </div>

      {/* Game selection */}
      <div>
        <p className="text-sm font-semibold mb-3 text-foreground">Choose Game</p>
        <div className="grid grid-cols-2 gap-3">
          {GAME_OPTIONS.map(g => (
            <button key={g.type} onClick={() => setSelectedGame(g.type)}
              className="p-4 rounded-xl text-left transition-all hover:scale-105"
              style={{
                background: selectedGame === g.type ? 'rgba(124,58,237,0.15)' : 'rgba(255,255,255,0.03)',
                border: `2px solid ${selectedGame === g.type ? '#7C3AED' : 'rgba(255,255,255,0.08)'}`,
              }}>
              <div className="mb-2">{g.icon}</div>
              <p className="text-sm font-bold text-foreground">{g.label}</p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{g.desc}</p>
              <div className="flex items-center gap-3 mt-2">
                <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                  <Clock size={10} /> {g.duration}
                </span>
                <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                  <Trophy size={10} /> {g.rounds} rounds
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Entry mode */}
      <div>
        <p className="text-sm font-semibold mb-3 text-foreground">Entry Mode</p>
        <div className="grid grid-cols-2 gap-3">
          {(['free', 'paid'] as const).map(mode => (
            <button key={mode} onClick={() => setEntryMode(mode)}
              className="p-4 rounded-xl text-left transition-all"
              style={{
                background: entryMode === mode ? 'rgba(124,58,237,0.15)' : 'rgba(255,255,255,0.03)',
                border: `2px solid ${entryMode === mode ? '#7C3AED' : 'rgba(255,255,255,0.08)'}`,
              }}>
              <div className="flex items-center gap-2 mb-1">
                {mode === 'free' ? <Zap size={16} style={{ color: '#10B981' }} /> : <DollarSign size={16} style={{ color: '#F59E0B' }} />}
                <span className="text-sm font-bold text-foreground capitalize">{mode === 'free' ? 'Free Play' : 'Paid Entry'}</span>
              </div>
              {mode === 'free' ? (
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Play for XP, badges & leaderboard rank</p>
              ) : (
                <div>
                  <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Entry: $5.00 · Prize pool: $9.00</p>
                  <p className="text-xs mt-0.5" style={{ color: '#F59E0B' }}>Platform fee: 10% ($1.00)</p>
                </div>
              )}
            </button>
          ))}
        </div>
        {entryMode === 'paid' && (
          <div className="mt-2 p-3 rounded-xl text-xs" style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', color: '#F59E0B' }}>
            ⚠️ Confirm Entry — $5.00 will be locked from your wallet. Prize pool: $9.00. Platform fee: $1.00. Funds released after server-validated result.
          </div>
        )}
      </div>

      {/* Start button */}
      <button onClick={() => onStart(selectedGame)}
        className="w-full py-4 rounded-xl text-base font-black flex items-center justify-center gap-3 transition-all hover:scale-105 hover:shadow-lg"
        style={{ background: 'linear-gradient(135deg, #7C3AED, #06B6D4)', color: '#fff', boxShadow: '0 0 30px rgba(124,58,237,0.3)' }}>
        <Swords size={20} />
        {entryMode === 'paid' ? 'Confirm Entry & Start Match — $5.00' : 'Start Free Match'}
        <ChevronRight size={18} />
      </button>

      <p className="text-xs text-center" style={{ color: 'var(--muted-foreground)', opacity: 0.6 }}>
        All moves validated server-side · Results immutable · Fair play enforced
      </p>
    </div>
  );
}

function PlayerCard({ player, rankColors, side, isOpponent }: { player: Player; rankColors: Record<string, string>; side: 'left' | 'right'; isOpponent?: boolean }) {
  return (
    <div className={`flex flex-col items-center gap-2 ${side === 'right' ? 'items-end' : 'items-start'}`}>
      <div className="relative">
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-xl font-black"
          style={{ background: isOpponent ? 'linear-gradient(135deg, #EF4444, #F59E0B)' : 'linear-gradient(135deg, #7C3AED, #06B6D4)', color: '#fff' }}>
          {player.avatar}
        </div>
        <span className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-gray-900 bg-green-400" />
      </div>
      <div className={`text-${side === 'right' ? 'right' : 'left'}`}>
        <p className="text-sm font-bold text-foreground">{player.name}</p>
        <p className="text-xs font-semibold" style={{ color: rankColors[player.rank] }}>{player.rank} · {player.rating}</p>
        <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{player.country} · {player.winRate}% WR</p>
      </div>
    </div>
  );
}
